import { Link } from "react-router-dom";
import "@/App.css";
import { 
  ShieldAlert, 
  MapPin,
  Phone,
  DollarSign,
  CheckCircle,
  ChevronRight,
  Briefcase,
  Clock,
  Globe,
  AlertTriangle,
  ArrowLeft,
  FileText
} from "lucide-react";

function JobPosting() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-sm border-b border-white/10">
        <div className="section-container flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3">
            <ShieldAlert className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
            <span 
              className="font-bold text-xl tracking-tight uppercase"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="logo-text"
            >
              Simon Advanced Security
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="footer-link hover:text-orange-500" data-testid="nav-home">
              Home
            </Link>
            <Link to="/jobs" className="footer-link hover:text-orange-500" data-testid="nav-jobs">
              All Jobs
            </Link>
            <a 
              href="tel:+18008241906" 
              className="btn-primary text-sm py-2 px-4"
              data-testid="nav-call-btn"
            >
              Call Now
            </a>
          </div>
        </div>
      </nav>

      {/* Back Link */}
      <div className="pt-24 pb-4 bg-[#0A0A0A]">
        <div className="section-container">
          <Link 
            to="/jobs" 
            className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-400 transition-colors text-sm"
            data-testid="back-to-jobs"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to All Jobs
          </Link>
        </div>
      </div>

      {/* Job Header */}
      <section className="py-8 bg-[#0A0A0A] relative overflow-hidden" data-testid="job-header">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(249,115,22,0.1) 50px, rgba(249,115,22,0.1) 51px),
                              repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(249,115,22,0.1) 50px, rgba(249,115,22,0.1) 51px)`
          }} />
        </div>
        
        <div className="section-container relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-red-500/20 border border-red-500/50 text-red-400 text-xs px-3 py-1 uppercase tracking-wider">
                High Threat
              </span>
              <span className="bg-orange-500/20 border border-orange-500/50 text-orange-400 text-xs px-3 py-1 uppercase tracking-wider">
                Full-Time Contract
              </span>
            </div>
            
            <h1 
              className="text-3xl md:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white mb-6"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="job-title"
            >
              Country Director, High-Threat Operations – Venezuela
            </h1>

            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 text-gray-400">
                <MapPin className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                <span className="text-sm">Venezuela</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <DollarSign className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                <span className="text-sm">$438,000 / Year (USD)</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Briefcase className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                <span className="text-sm">1099 Contractor</span>
              </div>
            </div>

            <a 
              href="tel:+18008241906"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="apply-btn-top"
            >
              <Phone className="w-5 h-5" />
              Apply Now: 800-824-1906
            </a>
          </div>
        </div>
      </section>

      {/* Job Content */}
      <section className="py-16 bg-[#121212]">
        <div className="section-container">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Overview */}
              <div data-testid="job-overview">
                <h2 
                  className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mb-6"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Position Overview
                </h2>
                <div className="prose prose-invert max-w-none">
                  <p className="text-gray-400 text-base leading-relaxed mb-4">
                    Simon Advanced Security is seeking a <strong className="text-white">Country Director</strong> to hold operational command of all in-country operations across several concurrent client programs in Venezuela.
                  </p>
                  <p className="text-gray-400 text-base leading-relaxed mb-4">
                    Venezuela is a post-transition, post-earthquake operating environment with an unstable power grid, degraded medical infrastructure, active armed groups, routine checkpoint friction, and elevated kidnap and extortion risk.
                  </p>
                  <div className="bg-red-500/10 border border-red-500/30 p-4 my-6">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-300 text-sm">
                        <strong className="text-red-400">Critical Notice:</strong> There is no host-nation, military, or government agency support structure behind these operations. Candidates must have demonstrated prior experience directing high-threat security operations with little to no support systems in place.
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-400 text-base leading-relaxed">
                    Vacation days and flexible travel is offered with this position; however, the Country Director will be expected to be on-call at all times and available to manage issues that may arise with operations.
                  </p>
                </div>
              </div>

              {/* Duties & Responsibilities */}
              <div data-testid="job-duties">
                <h2 
                  className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mb-6"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Duties & Responsibilities
                </h2>
                <ul className="space-y-4">
                  {[
                    "Exercise operational command of all security operations in Venezuela across multiple concurrent client programs",
                    "Determine force posture (overt, low-profile, or covert protection; static or mobile; team or solo advisor) and adjust as conditions dictate",
                    "Serve as senior representative in country to clients, embassy RSOs (where appropriate), insurers, local counsel, and authorities",
                    "Develop and maintain site security plans, movement protocols, PACE communications plans, medical and CASEVAC plans, and emergency action and country evacuation plans",
                    "Conduct rehearsals and readiness validation for all contingency plans",
                    "Enforce standards, rules for the use of force, and discipline",
                    "Command serious incident response: kidnap and extortion response coordination with crisis cell and insurers, medical emergencies, natural disaster response, and evacuation execution",
                    "Reports directly to the Director of Operations"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <ChevronRight className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span className="text-gray-300 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Minimum Qualifications */}
              <div data-testid="job-requirements">
                <h2 
                  className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mb-6"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Minimum Qualifications
                </h2>
                <p className="text-red-400 text-sm mb-6 uppercase tracking-wider">
                  Do not apply without meeting ALL minimum qualification requirements
                </p>
                <ul className="space-y-4">
                  {[
                    { text: "15+ years of high-threat security operations experience, with recent operational security experience in Venezuela", critical: true },
                    { text: "Significant private sector experience conducting operations in non-permissive environments WITHOUT government support infrastructure (no QRF, no MEDEVAC chain, no diplomatic top cover). This will be verified in detail during screening.", critical: true },
                    { text: "Demonstrated command across the full operational spectrum: large multi-site guard forces, small teams, solo low-profile advisory roles, overt and covert protective postures, and static and mobile operations", critical: false },
                    { text: "Prior experience leading or directing real-world crisis incidents (not exercises/training); hostage rescue experience is heavily weighted", critical: false },
                    { text: "High-level Spanish language proficiency", critical: true },
                    { text: "Must be medically and physically fit for extended austere deployment", critical: false },
                    { text: "Qualified candidates must pass extensive vetting process", critical: false },
                    { text: "Strong judgment, professionalism, and discretion", critical: false },
                    { text: "Valid U.S. Passport with current availability", critical: false }
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${item.critical ? 'text-red-500' : 'text-orange-500'}`} strokeWidth={1.5} />
                      <span className={`text-sm ${item.critical ? 'text-white font-medium' : 'text-gray-300'}`}>{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Preferred Qualifications */}
              <div data-testid="job-preferred">
                <h2 
                  className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mb-6"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Preferred Qualifications
                </h2>
                <p className="text-gray-400 text-sm mb-6">
                  In addition to meeting all required qualifications, meeting any of the below will increase your overall competitiveness in the selection process:
                </p>
                <ul className="space-y-4">
                  {[
                    "Prior U.S. military SOF backgrounds",
                    "Experience supporting corporate and/or UHNW principals in a high-threat environment",
                    "Familiarity with executive aviation logistics"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-orange-500/60 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span className="text-gray-400 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Required Skills */}
              <div data-testid="job-skills">
                <h2 
                  className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mb-6"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Required Skills
                </h2>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Executive Protection",
                    "Guard Operations",
                    "Logistics",
                    "Medical",
                    "Operations",
                    "PSD - Foreign",
                    "Security Management"
                  ].map((skill, index) => (
                    <span 
                      key={index}
                      className="bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs px-3 py-2 uppercase tracking-wider"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Apply Card */}
                <div className="bg-black/40 border border-white/10 p-6" data-testid="apply-card">
                  <h3 
                    className="text-xl font-bold uppercase tracking-tight text-white mb-4"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    Apply For This Position
                  </h3>
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Compensation</p>
                        <p className="text-white text-lg font-bold">$410,000/year</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Location</p>
                        <p className="text-white">Venezuela</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Briefcase className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Employment Type</p>
                        <p className="text-white">Full-Time Contractor (1099)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Start Date</p>
                        <p className="text-white">Discussed with employer</p>
                      </div>
                    </div>
                  </div>
                  <a 
                    href="tel:+18008241906"
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    data-testid="apply-btn-sidebar"
                  >
                    <Phone className="w-5 h-5" />
                    800-824-1906
                  </a>
                </div>

                {/* Security Notice */}
                <div className="bg-orange-500/10 border border-orange-500/30 p-6">
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-bold text-sm mb-2">Vetting Process</h4>
                      <p className="text-gray-400 text-xs">
                        All qualified candidates will be required to pass an extensive vetting and background verification process.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Other Jobs Link */}
                <Link 
                  to="/jobs"
                  className="block bg-black/40 border border-white/10 p-6 hover:border-orange-500/50 transition-colors"
                  data-testid="other-jobs-link"
                >
                  <h4 className="text-white font-bold text-sm mb-2">View Other Positions</h4>
                  <p className="text-gray-400 text-xs">
                    Explore entry-level security positions in Orlando, FL and New Jersey starting at $18/HR.
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#0A0A0A] border-t border-white/10">
        <div className="section-container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-3">
              <ShieldAlert className="w-6 h-6 text-orange-500" strokeWidth={1.5} />
              <span 
                className="font-bold text-lg tracking-tight uppercase text-white"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Simon Advanced Security
              </span>
            </Link>
            <p className="text-gray-500 text-xs tracking-widest">
              © {new Date().getFullYear()} ALL RIGHTS RESERVED
            </p>
            <div className="flex items-center gap-6">
              <Link to="/" className="footer-link" data-testid="footer-home">Home</Link>
              <Link to="/jobs" className="footer-link" data-testid="footer-jobs">Careers</Link>
              <a href="tel:+18008241906" className="footer-link" data-testid="footer-call">Call Us</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default JobPosting;
